import { useState, useEffect } from 'react';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  ConfirmationDialog,
} from '../../../common';
import DebtEditHeader from './DebtEditHeader';
import DebtEditTable from './DebtEditTable';
import DebtEditControls from './DebtEditControls';
import DebtEditTotals from './DebtEditTotals';
import { 
  getCurrentDate,
  formatCouponsForUpdate,
  formatCurrency,
} from '../utils/updateDebtUtils';
import { Box, Typography } from '@mui/material';

const UpdateDebtEditDialog = ({ 
  open, 
  onClose, 
  onSave, 
  selectedItem, 
  plans 
}) => {
  const [secondExpirationDate, setSecondExpirationDate] = useState(getCurrentDate());
  const [modifiedCoupons, setModifiedCoupons] = useState([]);
  const [payChecks, setPayChecks] = useState([]);
  const [regularizeChecks, setRegularizeChecks] = useState([]);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);

  useEffect(() => {
    if (selectedItem?.coupons) {
      setPayChecks(selectedItem.coupons.map(() => false));
      setRegularizeChecks(selectedItem.coupons.map(() => false));
      setModifiedCoupons([]);
      setSecondExpirationDate(getCurrentDate());
      setConfirmationOpen(false);
      setPendingUpdateData(null);
    }
  }, [selectedItem]);

  const handleModifiedCouponsChange = (newModifiedCoupons) => {
    setModifiedCoupons(newModifiedCoupons);
  };

  const handlePayChecksChange = (newPayChecks) => {
    setPayChecks(newPayChecks);
  };

  const handleRegularizeChecksChange = (newRegularizeChecks) => {
    setRegularizeChecks(newRegularizeChecks);
  };

  const handleSave = () => {
    const formattedData = formatCouponsForUpdate(
      selectedItem.coupons,
      modifiedCoupons,
      payChecks,
      regularizeChecks
    );

    if (formattedData.coupons.length > 0) {
      setPendingUpdateData(formattedData);
      setConfirmationOpen(true);
    }
  };

  const handleConfirmSave = () => {
    if (!pendingUpdateData?.coupons?.length) return;

    onSave(pendingUpdateData);
    setConfirmationOpen(false);
    setPendingUpdateData(null);
    onClose();
  };

  const handleCloseConfirmation = () => {
    setConfirmationOpen(false);
  };

  const getConfirmationSummary = () => {
    const couponsToUpdate = pendingUpdateData?.coupons || [];
    const payCount = couponsToUpdate.filter((coupon) => coupon.action === 'pago').length;
    const regularizeCount = couponsToUpdate.filter((coupon) => coupon.action === 'regulariza').length;

    const totalPay = couponsToUpdate
      .filter((coupon) => coupon.action === 'pago')
      .reduce((total, coupon) => total + (Number(coupon.third_due_amount) || 0), 0);

    const totalRegularize = couponsToUpdate
      .filter((coupon) => coupon.action === 'regulariza')
      .reduce((total, coupon) => total + (Number(coupon.interest) || 0), 0);

    return {
      couponsToUpdate,
      payCount,
      regularizeCount,
      totalPay,
      totalRegularize,
    };
  };

  if (!selectedItem) return null;

  const confirmationSummary = getConfirmationSummary();

  return (
    <>
      <DialogContainer
        open={open}
        onClose={onClose}
        size="large"
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            maxWidth: '95vw',
            width: '95vw',
          },
        }}
      >
        <DialogTitle>DEUDAS A LA FECHA</DialogTitle>

        <DialogBody sx={{ 
          p: 0, 
          display: 'flex',
          flexDirection: 'column',
          height: '75vh',
          overflow: 'hidden',
        }}>
          <DebtEditHeader 
            selectedItem={selectedItem}
            plans={plans}
          />

          <DebtEditControls
            secondExpirationDate={secondExpirationDate}
            setSecondExpirationDate={setSecondExpirationDate}
            selectedItem={selectedItem}
            payChecks={payChecks}
            regularizeChecks={regularizeChecks}
            onModifiedCouponsChange={handleModifiedCouponsChange}
            onPayChecksChange={handlePayChecksChange}
            onRegularizeChecksChange={handleRegularizeChecksChange}
          />

          <Box sx={{ 
            flexGrow: 1, 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}>
            <DebtEditTable
              coupons={selectedItem.coupons}
              modifiedCoupons={modifiedCoupons}
              payChecks={payChecks}
              regularizeChecks={regularizeChecks}
              onModifiedCouponsChange={handleModifiedCouponsChange}
              onPayChecksChange={handlePayChecksChange}
              onRegularizeChecksChange={handleRegularizeChecksChange}
            />
          </Box>

          <DebtEditTotals
            coupons={selectedItem.coupons}
            modifiedCoupons={modifiedCoupons}
            payChecks={payChecks}
            regularizeChecks={regularizeChecks}
          />
        </DialogBody>

        <DialogActions 
          handleClose={onClose} 
          handleSave={handleSave}
        />
      </DialogContainer>

      <ConfirmationDialog
        open={confirmationOpen}
        onClose={handleCloseConfirmation}
        onConfirm={handleConfirmSave}
        title="Confirmar actualización de deuda"
        message={`Se actualizarán ${confirmationSummary.couponsToUpdate.length} cupón(es) de la orden ${selectedItem.header.contract_number}.`}
        confirmText="Confirmar"
        cancelText="Volver"
        confirmVariant="warning"
        details={
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography variant="body2">
              Pagar: {confirmationSummary.payCount} cupón(es) por {formatCurrency(confirmationSummary.totalPay)}
            </Typography>
            <Typography variant="body2">
              Regularizar: {confirmationSummary.regularizeCount} cupón(es) por {formatCurrency(confirmationSummary.totalRegularize)}
            </Typography>
          </Box>
        }
      />
    </>
  );
};

export default UpdateDebtEditDialog;
